
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, TrendingUp, Wallet, BarChart3 } from 'lucide-react';
import { StatsCard } from '@/components/dashboard/StatsCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAffiliateInfo, useAffiliateStats } from '@/hooks/useAffiliate';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState<"7d" | "30d" | "ytd" | "all">("30d");
  const { data: affiliateInfo, isLoading: isLoadingInfo } = useAffiliateInfo();
  const { data: stats, isLoading: isLoadingStats } = useAffiliateStats(timeframe);
  const isMobile = useIsMobile();

  // If user is not an affiliate yet, show the onboarding screen
  if (!isLoadingInfo && !affiliateInfo) {
    return (
      <div className="flex flex-col items-center justify-center py-6 md:py-12 px-4">
        <div className="text-center max-w-md">
          <h1 className="text-xl md:text-3xl font-bold mb-3 md:mb-4">Join Our Affiliate Program</h1>
          <p className={`mb-4 md:mb-6 ${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
            Earn commission by referring new users to our platform. 
            Promote our trading bots and earn up to 10% of their subscriptions.
          </p>
          <Button size={isMobile ? "sm" : "lg"} onClick={() => navigate('/affiliate/onboarding')} className="bg-botscheme-coral-orange hover:bg-botscheme-coral-orange/90">
            Apply Now
          </Button>
        </div>
      </div>
    );
  }

  // If the user is a pending affiliate
  if (affiliateInfo?.status === "pending") {
    return (
      <div className="max-w-4xl mx-auto py-4 md:py-8 px-3 md:px-4">
        <Card className="card-scheme-amber-gold">
          <CardHeader className={isMobile ? "p-4" : ""}>
            <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>Application Under Review</CardTitle>
            <CardDescription className={isMobile ? "text-xs" : "text-sm"}>
              Thank you for your interest in our affiliate program
            </CardDescription>
          </CardHeader>
          <CardContent className={`pt-3 md:pt-6 ${isMobile ? "p-4" : ""}`}>
            <div className="flex flex-col items-center text-center space-y-3 md:space-y-4 py-4 md:py-8">
              <div className={`p-2 md:p-3 rounded-full bg-botscheme-amber-gold/20 text-botscheme-amber-gold`}>
                <Users size={isMobile ? 24 : 36} />
              </div>
              <h3 className={`${isMobile ? "text-lg" : "text-xl"} font-semibold`}>Your application is being reviewed</h3>
              <p className={`max-w-md ${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
                We're currently reviewing your affiliate application. This process typically takes 1-2 business days.
                You'll receive an email notification when your application has been approved.
              </p>
              <Button variant="outline" size={isMobile ? "sm" : "default"} onClick={() => navigate('/')}>
                Return to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoadingInfo || isLoadingStats) {
    return (
      <div className="space-y-4 md:space-y-8">
        <div>
          <Skeleton className="h-7 md:h-10 w-40 md:w-60 mb-2" />
          <Skeleton className="h-4 w-60 md:w-96" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
          <Skeleton className="h-24 md:h-32" />
          <Skeleton className="h-24 md:h-32" />
          <Skeleton className="h-24 md:h-32" />
        </div>
        <Skeleton className="h-60 md:h-80" />
      </div>
    );
  }

  // Render a specific tab content based on the timeframe
  const renderTabContent = (timeframeValue: string) => {
    return (
      <div className="mt-4 md:mt-6 space-y-4 md:space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          <Card className="card-scheme-coral-orange">
            <CardHeader className={`pb-2 ${isMobile ? "p-3" : "pb-2"}`}>
              <CardDescription className={isMobile ? "text-xs" : ""}>Signups</CardDescription>
              <div className="flex items-center justify-between">
                <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>{stats?.signups || 0}</CardTitle>
                <Users className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-botscheme-coral-orange`} />
              </div>
            </CardHeader>
            <CardContent className={isMobile ? "p-3 pt-0" : ""}>
              <div className={`${isMobile ? "text-2xs" : "text-xs"} text-muted-foreground`}>
                {timeframeValue === "7d" ? "Last 7 days" : 
                 timeframeValue === "30d" ? "Last 30 days" : 
                 timeframeValue === "ytd" ? "Year to date" : "All time"}
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-scheme-ocean-blue">
            <CardHeader className={`pb-2 ${isMobile ? "p-3" : "pb-2"}`}>
              <CardDescription className={isMobile ? "text-xs" : ""}>Conversion Rate</CardDescription>
              <div className="flex items-center justify-between">
                <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>{stats?.conversionRate || 0}%</CardTitle>
                <BarChart3 className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-botscheme-ocean-blue`} />
              </div>
            </CardHeader>
            <CardContent className={isMobile ? "p-3 pt-0" : ""}>
              <div className={`${isMobile ? "text-2xs" : "text-xs"} text-muted-foreground`}>
                {stats?.conversions || 0} conversions
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-scheme-royal-purple">
            <CardHeader className={`pb-2 ${isMobile ? "p-3" : "pb-2"}`}>
              <CardDescription className={isMobile ? "text-xs" : ""}>Total Earned</CardDescription>
              <div className="flex items-center justify-between">
                <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>${stats?.totalCommission || 0}</CardTitle>
                <TrendingUp className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-botscheme-royal-purple`} />
              </div>
            </CardHeader>
            <CardContent className={isMobile ? "p-3 pt-0" : ""}>
              <div className={`${isMobile ? "text-2xs" : "text-xs"} text-muted-foreground`}>
                All time
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className={isMobile ? "p-4" : ""}>
            <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>Performance Overview</CardTitle>
            <CardDescription className={isMobile ? "text-xs" : "text-sm"}>
              Your affiliate performance metrics
            </CardDescription>
          </CardHeader>
          <CardContent className={`${isMobile ? "h-[200px] p-4 pt-0" : "h-[300px]"} flex items-center justify-center`}>
            <div className="text-center space-y-3 md:space-y-4">
              <BarChart3 size={isMobile ? 32 : 48} className="mx-auto text-muted-foreground" />
              <div>
                <p className={`${isMobile ? "text-xs" : ""} text-muted-foreground`}>
                  Detailed performance charts are coming soon
                </p>
                <Button 
                  className="mt-2 md:mt-4" 
                  variant="outline" 
                  size={isMobile ? "sm" : "default"}
                  onClick={() => navigate('/affiliate/reports')}
                >
                  View Reports
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <div>
        <h1 className="text-xl md:text-3xl font-bold">Affiliate Dashboard</h1>
        <p className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground mt-1`}>
          Track your referrals and earned commissions
        </p>
      </div>

      <Tabs defaultValue="30d" onValueChange={(value) => setTimeframe(value as any)}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <TabsList className={`bg-background/80 border ${isMobile ? "w-full grid grid-cols-2 h-auto" : ""}`}>
            <TabsTrigger value="7d" className={isMobile ? "text-xs py-1.5" : ""}>7 Days</TabsTrigger>
            <TabsTrigger value="30d" className={isMobile ? "text-xs py-1.5" : ""}>30 Days</TabsTrigger>
            <TabsTrigger value="ytd" className={isMobile ? "text-xs py-1.5" : ""}>Year to Date</TabsTrigger>
            <TabsTrigger value="all" className={isMobile ? "text-xs py-1.5" : ""}>All Time</TabsTrigger>
          </TabsList>
          
          {!isMobile && (
            <div className="space-x-2">
              <Button variant="outline" onClick={() => navigate('/affiliate/links')}>
                Manage Links
              </Button>
              <Button variant="outline" onClick={() => navigate('/affiliate/reports')}>
                Reports
              </Button>
            </div>
          )}
        </div>

        {isMobile && (
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate('/affiliate/links')}>
              Manage Links
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => navigate('/affiliate/reports')}>
              Reports
            </Button>
          </div>
        )}

        <TabsContent value="7d">
          {renderTabContent("7d")}
        </TabsContent>

        <TabsContent value="30d">
          {renderTabContent("30d")}
        </TabsContent>

        <TabsContent value="ytd">
          {renderTabContent("ytd")}
        </TabsContent>

        <TabsContent value="all">
          {renderTabContent("all")}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AffiliateDashboard;
